import React, { useRef, useState } from 'react'
import { HiddenInput, RemoveButton, UploadArea, UploadContainer, UploadContent, UploadIcon, UploadText, FilePreviewContainer, FilePreviewImage, FilePreviewInfo, FullScreenModal, FullScreenImage, CloseButton } from './style'
import { FileUp, X, ImageIcon } from 'lucide-react'
import { CertificateType } from '../../../pages/ClientPanel/Certificates/CreateCertificate'
import ApiService from '../../../services/ApiService'
import { useOutletContext } from 'react-router-dom'
import { CertificateOutletType } from '../../../pages/ClientPanel/Certificates'

interface SupplierFileComponentProps {
  certificate: CertificateType
  setCertificate: React.Dispatch<React.SetStateAction<CertificateType>>
}

interface OCRResponse {
  id: number
  field: string
  value: string
}

const SupplierFileComponent = ({ certificate, setCertificate }: SupplierFileComponentProps) => {
  const [ isDragActive, setIsDragActive ] = useState(false)
  const [ isFullScreenOpen, setIsFullScreenOpen ] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { setLoading } = useOutletContext<CertificateOutletType>()

  const sendFileToOCR = (file: File) => {
    setLoading(true)
    const formData = new FormData()
    formData.append('file', file)
    
    console.log('Enviando arquivo para OCR:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    })
    
    ApiService.ocr.process(formData)
    .then(response => {
      console.log('Resposta do OCR:', response.data)
      setCertificate(prev => ({
        ...prev,
        columnsInStructureWithValues: prev.columnsInStructureWithValues.map(column => {
          const ocrResponse:OCRResponse[] = response.data
          return {
            ...column,
            value: ocrResponse.find(ocr => ocr.id === column.id)?.value || ''
          }
        })
      }))
    })
    .catch(error => {
      console.error('Erro detalhado do OCR:', error)
      console.error('Status:', error.response?.status)
      console.error('Data:', error.response?.data)
      console.error('Headers:', error.response?.headers)
      
      let errorMessage = 'Erro ao processar o arquivo. '
      
      if (error.response?.status === 400) {
        errorMessage += 'Arquivo inválido ou formato não suportado. Verifique se o arquivo é uma imagem JPEG ou PNG válida.'
      } else if (error.response?.status === 413) {
        errorMessage += 'Arquivo muito grande. Tente com uma imagem menor.'
      } else if (error.response?.status >= 500) {
        errorMessage += 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.'
      } else {
        errorMessage += 'Por favor, tente novamente.'
      }
      
      alert(errorMessage)
      handleRemove()
    })
    .finally(() => {
      setLoading(false)
    })
  }

  const handleFileSelect = async (file: File) => {
    console.log('Arquivo selecionado:', {
      name: file.name,
      size: file.size,
      type: file.type
    })
    
    if (!file) {
      alert('Nenhum arquivo selecionado.')
      return
    }
    
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      alert('Arquivo muito grande. O tamanho máximo é 10MB.')
      return
    }
    
    if (!(file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg')) {
      alert('Somente arquivos JPEG ou PNG podem ser carregados.')
      return
    }
    
    setCertificate(prev => ({
      ...prev,
      supplierFile: file
    }))
    sendFileToOCR(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(false)
    
    const file = e.dataTransfer.files[0]
    handleFileSelect(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(true)
  }

  const handleDragLeave = () => {
    setIsDragActive(false)
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0])
    }
  }

  const handleRemove = () => {
      setCertificate((prev:any) => ({
        ...prev,
        supplierFile: null
      }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleImageClick = () => {
    setIsFullScreenOpen(true)
  }

  const handleCloseFullScreen = () => {
    setIsFullScreenOpen(false)
  }

  const hasFile = certificate.supplierFile ? true : false
  const fileName = certificate.supplierFile instanceof File ? certificate.supplierFile.name : 'Certificado de origem original'
  
  const getFileSrc = () => {
    if (certificate.supplierFile instanceof File) {
      return URL.createObjectURL(certificate.supplierFile)
    }
    if (typeof certificate.supplierFile === 'string') {
      return certificate.supplierFile
    }
    return ''
  }

  const getFileSize = () => {
    if (certificate.supplierFile instanceof File) {
      return (certificate.supplierFile.size / 1024 / 1024).toFixed(1)
    }
    return 'N/A'
  }
  
  return(
    <>
      <UploadContainer>
        {!hasFile ? (
          <UploadArea
            isDragActive={isDragActive}
            hasFile={hasFile}
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept=".jpg, .jpeg, .png"
              onChange={handleChange}
            />
            
            <UploadContent>
              <UploadIcon>
                <FileUp />
              </UploadIcon>
              <UploadText>
                Certificado do fornecedor
                <br />
                Somente JPEG ou PNG
              </UploadText>
            </UploadContent>
          </UploadArea>
        ) : (
          <FilePreviewContainer>
            {!certificate.heatNumber?.originCertificateId &&
              <RemoveButton onClick={handleRemove}>
                <X size={18} strokeWidth={3} />
              </RemoveButton>
            }
            <FilePreviewImage 
              draggable={false} 
              src={getFileSrc()} 
              alt={fileName}
              onClick={handleImageClick}
              style={{ cursor: 'pointer' }}
            />
            <FilePreviewInfo>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <ImageIcon size={18} />
                <span>{getFileSize()} MB</span>
              </div>
              <div style={{fontWeight: '500', color: '#374151'}}>
                {fileName}
              </div>
            </FilePreviewInfo>
          </FilePreviewContainer>
        )}
      </UploadContainer>

      {isFullScreenOpen && (
        <FullScreenModal onClick={handleCloseFullScreen}>
          <CloseButton onClick={handleCloseFullScreen}>
            <X size={24} />
          </CloseButton>
          <FullScreenImage 
            src={getFileSrc()} 
            alt={fileName}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        </FullScreenModal>
      )}
    </>
  )
}

export default SupplierFileComponent
